/** Переход, остановленный до подтверждения: адрес History или шаг «Назад»/«Вперёд». */
export type BlockedNavigation =
    { kind: 'url'; url: string; replace: boolean; state: unknown } | { kind: 'delta'; delta: number };

export interface INavigationGuard {
    /** Остановить ли уход; вызывается в момент перехода. */
    shouldBlock: () => boolean;
    /** Уход остановлен: спросить и, если согласились, повторить его (`bypassNextNavigation` + navigate). */
    onBlock: (navigation: BlockedNavigation) => void;
    /**
     * Уход ли со страницы переход между адресами. По умолчанию — смена пути: параметры и якорь меняет сама страница
     * (фильтры, `?open=`, вкладки) и свою правку бережёт сама.
     */
    leaves?: (from: URL, to: URL) => boolean;
}

type HistoryMethod = (this: History, data: unknown, unused: string, url?: string | URL | null) => void;

interface IPatchableHistory {
    pushState: HistoryMethod;
    replaceState: HistoryMethod;
}

const patchableHistory = () => window.history as IPatchableHistory;

/*
 * Перехват ухода со страницы внутри приложения. useBlocker из react-router требует data router, а приложение
 * на BrowserRouter, поэтому переходы ловятся на уровне History: pushState/replaceState (Link, NavLink,
 * любой navigate) и popstate («Назад»/«Вперёд», navigate(-1)). Действует последний добавленный guard.
 */
const guards: INavigationGuard[] = [];
let originalPushState: HistoryMethod | null = null;
let originalReplaceState: HistoryMethod | null = null;
let bypassCount = 0;
/** Запись истории, на которой стоит страница: от неё считается шаг «Назад»/«Вперёд» */
let currentEntry: { idx: number | null; href: string } = { idx: null, href: '' };
/** Индекс, куда возвращает отменённый «Назад»: этот popstate только восстанавливает адрес */
let restoringIdx: number | null = null;

const activeGuard = (): INavigationGuard | undefined => guards[guards.length - 1];

const toUrl = (url: string | URL): URL | null => {
    try {
        return new URL(String(url), window.location.href);
    } catch {
        return null;
    }
};

const pathChanged = (from: URL, to: URL) => from.pathname !== to.pathname;

/** Переход `from` → `to` уводит со страницы, по мнению guard'а */
const isLeaving = (guard: INavigationGuard, from: string | URL, to: string | URL) => {
    const source = toUrl(from);
    const target = toUrl(to);
    return source !== null && target !== null && (guard.leaves ?? pathChanged)(source, target);
};

/** Индекс записи, который react-router кладёт в history.state */
const historyIndex = (): number | null => {
    const idx = (window.history.state as { idx?: unknown } | null)?.idx;
    return typeof idx === 'number' ? idx : null;
};

const rememberCurrentEntry = () => {
    currentEntry = { idx: historyIndex(), href: window.location.href };
};

const guardHistoryMethod =
    (original: HistoryMethod, replace: boolean): HistoryMethod =>
    (data, unused, url) => {
        if (bypassCount > 0) {
            bypassCount--;
        } else {
            const guard = activeGuard();
            if (guard && url != null && isLeaving(guard, window.location.href, url) && guard.shouldBlock()) {
                guard.onBlock({ kind: 'url', url: String(url), replace, state: data });
                return;
            }
        }
        original.call(window.history, data, unused, url);
        rememberCurrentEntry();
    };

/** Отменённый шаг до роутера не доходит: адрес возвращается history.go(-delta), и этот возврат тоже гасится */
const onPopState = (event: PopStateEvent) => {
    if (!originalPushState) return;
    const idx = historyIndex();
    if (restoringIdx !== null) {
        const restoring = idx === restoringIdx;
        restoringIdx = null;
        if (restoring) {
            event.stopImmediatePropagation();
            return;
        }
    }
    if (bypassCount > 0) {
        bypassCount--;
        rememberCurrentEntry();
        return;
    }
    const guard = activeGuard();
    const from = currentEntry;
    if (
        !guard ||
        idx === null ||
        from.idx === null ||
        idx === from.idx ||
        !isLeaving(guard, from.href, window.location.href) ||
        !guard.shouldBlock()
    ) {
        rememberCurrentEntry();
        return;
    }
    event.stopImmediatePropagation();
    const delta = idx - from.idx;
    restoringIdx = from.idx;
    window.history.go(-delta);
    guard.onBlock({ kind: 'delta', delta });
};

let traversalListening = false;

/**
 * Слушатель «Назад»/«Вперёд» регистрируется до монтирования роутера: у слушателей на window Chrome соблюдает
 * порядок регистрации и capture не помогает, а react-router, получив popstate первым, синхронно перерисовывает
 * страницу (React 19 рендерит обновления popstate сразу) — guard размонтируется раньше, чем увидит событие.
 */
export function listenHistoryTraversal() {
    if (traversalListening) return;
    traversalListening = true;
    window.addEventListener('popstate', onPopState, true);
}

const installHistoryPatch = () => {
    if (originalPushState) return;
    const history = patchableHistory();
    originalPushState = history.pushState;
    originalReplaceState = history.replaceState;
    history.pushState = guardHistoryMethod(originalPushState, false);
    history.replaceState = guardHistoryMethod(originalReplaceState, true);
    listenHistoryTraversal();
    rememberCurrentEntry();
};

const uninstallHistoryPatch = () => {
    if (!originalPushState || !originalReplaceState) return;
    const history = patchableHistory();
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    originalPushState = null;
    originalReplaceState = null;
    bypassCount = 0;
    restoringIdx = null;
};

/** Перехватывать уход со страницы, пока не вызван результат. */
export const addNavigationGuard = (entry: INavigationGuard): (() => void) => {
    guards.push(entry);
    installHistoryPatch();
    return () => {
        const index = guards.lastIndexOf(entry);
        if (index >= 0) guards.splice(index, 1);
        if (!guards.length) uninstallHistoryPatch();
    };
};

/**
 * Следующий переход пропускается мимо guard'а. Вызывать прямо перед осознанным переходом, когда правки уже
 * сохранены или сброшены, но страница ещё не перерисовалась (isDirty в guard'е прежний). Без активного
 * guard'а ничего не делает.
 */
export function bypassNextNavigation() {
    if (originalPushState) bypassCount++;
}

/** URL из History → путь для navigate: без basename, иначе роутер добавит его второй раз */
export const toRouterPath = (url: string, basename: string) => {
    const { pathname, search, hash } = new URL(url, window.location.href);
    const base = basename.replace(/\/+$/, '').toLowerCase();
    const lower = pathname.toLowerCase();
    const inBase = base !== '' && (lower === base || lower.startsWith(`${base}/`));
    return `${inBase ? pathname.slice(base.length) || '/' : pathname}${search}${hash}`;
};

export const routerState = (data: unknown) => (data as { usr?: unknown } | null)?.usr;
